<template>
  <div class="col-12 col-xl-12">
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ t('attendance.my_attendance_title') }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner v-if="isLoading && attendances.length === 0" :message="t('attendances.loading')" class="py-5" />

          <template v-else>
            <div class="d-block d-xl-none">
              <div v-if="!isLoading && attendances.length === 0" class="text-center py-3 text-muted">
                <i class="fas fa-users-slash fa-2x mb-2"></i>
                <p>{{ t('attendance.no_attendance_records') }}</p>
              </div>

              <div v-for="attendance in attendances" :key="'mobile-' + attendance.id" class="card mb-3 mx-2">
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 class="mb-0 text-sm">{{ formatDate(attendance.date) }}</h6>
                      <p class="text-xs text-muted mb-0">
                        {{ getDayName(attendance.day) }}
                      </p>
                    </div>
                    <div>
                      <span class="badge badge-sm" :class="getStatusBadgeClass(attendance.status)">
                        {{ getStatusName(attendance.status) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('attendance.date') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('attendance.day') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ t('attendances.status') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!isLoading && attendances.length === 0">
                  <td colspan="3" class="text-center py-4 text-muted">
                    <i class="fas fa-users-slash fa-2x mb-2"></i>
                    <p>{{ t('attendance.no_attendance_records') }}</p>
                  </td>
                </tr>
                
                <template v-if="!isLoading">
                  <tr v-for="attendance in attendances" :key="attendance.id">
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ formatDate(attendance.date) }}
                      </p>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ getDayName(attendance.day) }}
                      </p>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm" :class="getStatusBadgeClass(attendance.status)">
                        {{ getStatusName(attendance.status) }}
                      </span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            
            <div class="d-flex justify-content-center mt-4">
              <Pagination
                :pagination="pagination"
                @page-changed="onPageChanged"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useToast } from 'vue-toastification';
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import Pagination from "@/components/common/PaginationControls.vue";
import api from "@/api";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const toast = useToast();

const attendances = ref([]);
const isLoading = ref(false);
const pagination = ref({
  currentPage: 1,
  perPage: 15,
  totalItems: 0,
  totalPages: 0,
  from: 0,
  to: 0
});

const formatDate = (dateString) => {
  if (!dateString) return '';
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('ar', options);
};

const getDayName = (day) => {
  const days = {
    'sunday': t('attendances.sunday'),
    'monday': t('attendances.monday'),
    'tuesday': t('attendances.tuesday'),
    'wednesday': t('attendances.wednesday'),
    'thursday': t('attendances.thursday'),
    'friday': t('attendances.friday'),
    'saturday': t('attendances.saturday')
  };
  return days[day.toLowerCase()] || day;
};

const getStatusName = (status) => {
  const statuses = {
    'present': t('attendances.present'),
    'absent': t('attendances.absent'),
    'late': t('attendances.late'),
    'excused': t('attendances.excused')
  };
  return statuses[status] || status;
};

const getStatusBadgeClass = (status) => {
  const classes = {
    'present': 'bg-success',
    'absent': 'bg-danger',
    'late': 'bg-warning',
    'excused': 'bg-info'
  };
  return classes[status] || 'bg-secondary';
};

const fetchAttendances = async () => {
  isLoading.value = true;
  try {
    const response = await api.get('/student/student-attendances', {
      params: {
        page: pagination.value.currentPage,
        per_page: pagination.value.perPage
      }
    });
    
    attendances.value = response.data.data;
    
    if (response.data.meta) {
      pagination.value = {
        currentPage: response.data.meta.current_page,
        perPage: response.data.meta.per_page,
        totalItems: response.data.meta.total,
        totalPages: response.data.meta.last_page,
        from: response.data.meta.from,
        to: response.data.meta.to
      };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || t('attendances.load_failed');
    toast.error(errorMsg);
  } finally {
    isLoading.value = false;
  }
};

const onPageChanged = (page) => {
  pagination.value.currentPage = page;
  fetchAttendances();
};

onMounted(() => {
  fetchAttendances();
});
</script> 